import Vue from 'vue';
import Vuex from 'vuex';
import { SEF_ROLES } from '@/config/roles';
import { ENGINEER_ROLES } from '@/config/periferice';
import { clearPhotoCache } from '@/components/files/photoCache';
import { clearPreferences } from '@/helper/preferences';

Vue.use(Vuex);

const withSubunits = (list, subunits) => [...list, ...subunits.filter(s => !list.some(d => d.name === s.name))];

export default new Vuex.Store({
    state: {
        token: localStorage.getItem('token') || '',
        user: JSON.parse(localStorage.getItem('user')) || null,
        isLoggedIn: !!localStorage.getItem('token'),
        approvalCount: null,
        historyPageState: {
            sortBy: 'id',
            sortDesc: true,
            visibleHeaders: [],
            search: '',
            selectedItem: null,
        },
    },
    mutations: {
        SET_TOKEN(state, token) {
            state.token = token;
            state.isLoggedIn = true;
        },
        SET_USER(state, data) {
            state.user = data;
            localStorage.setItem('user', JSON.stringify(data));
        },
        SET_APPROVAL_COUNT(state, count) {
            state.approvalCount = count;
        },
        LOGOUT(state) {
            state.token = '';
            state.user = null;
            state.approvalCount = null;
            state.isLoggedIn = false;
            localStorage.removeItem('token');
            localStorage.removeItem('user');
        },
        SET_HISTORY_PAGE_STATE(state, historyPageState) {
            state.historyPageState = historyPageState;
        },
    },
    actions: {
        logout({ commit }) {
            commit('LOGOUT');
            // decrypted label photos and the rest must not stay in this tab for the next person
            clearPhotoCache();
            clearPreferences();
        },
        saveHistoryPageState({ commit }, historyPageState) {
            commit('SET_HISTORY_PAGE_STATE', historyPageState);
        },
    },
    getters: {
        isLoggedIn: state => state.isLoggedIn,
        getUser: state => state.user,
        isAdmin: state => {
            return state.user.role === 'admin';
        },
        isUser: state => {
            return state.user.role === 'user';
        },
        isRector: state => {
            return state.user.role === 'rector';
        },
        isUserSef: state => {
            return state.user.role === 'usersef';
        },
        isEngineer: state => {
            return state.user.role === 'inginer';
        },
        isEngineerPC: state => {
            return state.user.role === 'inginer-pc';
        },
        isDtiSef: state => {
            return state.user.role === 'dtisef';
        },
        // Does the work of the requests whose engineer (requests.engineer_role) is `engineerRole`: the engineers
        // for their types, the admins for the types Configurare gives them. isEngineer stays inginer only.
        actsAsEngineerFor: state => engineerRole => {
            const role = state.user && state.user.role;
            return !!role && role === engineerRole && ENGINEER_ROLES.includes(role);
        },
        // May have engineer work at all (take over, Îndeplinit, price): engineers, and the admins
        doesEngineerWork: state => !!state.user && ENGINEER_ROLES.includes(state.user.role),

        // Departments of the logged-in user, main one first. Sessions from before memberships only have `department`.
        myDepartments: state => {
            const user = state.user;
            if (!user) return [];
            if (Array.isArray(user.departments) && user.departments.length) return user.departments;
            return user.department
                ? [{ id: null, name: user.department, isSef: SEF_ROLES.includes(user.role), isPrimary: true }]
                : [];
        },
        hasManyDepartments: (state, getters) => getters.myDepartments.length > 1,
        sefDepartments: (state, getters) => getters.myDepartments.filter(d => d.isSef),
        // Sub-units without a Șef of their own whose requests reach this Șef through the department tree (GET /auth/me)
        sefSubunits: state => {
            const list = state.user && state.user.sefSubunits;
            return Array.isArray(list) ? list.map(d => ({ id: d.id, name: d.name, from: d.from, isSef: true, inherited: true })) : [];
        },
        // Departments whose requests the user sees (chips of Demersuri / Istoria): theirs + those sub-units
        requestDepartments: (state, getters) => withSubunits(getters.myDepartments, getters.sefSubunits),
        // ... and whose requests they approve (chips of Aprobare)
        approvalDepartments: (state, getters) => withSubunits(getters.sefDepartments, getters.sefSubunits),
        approvalCount: state => state.approvalCount,

        getHistoryPageState: state => state.historyPageState,
    },
});